/**
 * Suhaani Weaves — product page behaviour.
 *
 * Variant selection, media sync and gallery navigation. All variant facts
 * (ids, availability, prices) come from Shopify and are rendered into the
 * page by Liquid; this file only reads them.
 */
(function () {
  'use strict';

  const U = window.Urbanshop || {};

  const strings = (function () {
    const node = document.getElementById('ThemeStrings');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  })();

  /* ------------------------------------------------------------------ *
   * Variant picker
   * ------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-json]');
      if (!json) return;

      try {
        this.variants = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }

      this.idField = this.querySelector('[data-variant-id]');
      this.fieldsets = Array.from(this.querySelectorAll('[data-option-index]'));
      this.productUrl = this.getAttribute('data-product-url');

      this.addEventListener('change', () => this.onChange());
      this.refreshAvailability();
    }

    /** The option values currently ticked, in option order. */
    get selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, index) => value === options[index])
      );

      this.updateLegends();
      this.refreshAvailability();
      this.applyVariant(variant);
    }

    updateLegends() {
      this.fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        const output = fieldset.querySelector('[data-option-selected]');
        if (checked && output) output.textContent = checked.value;
      });
    }

    /**
     * Marks every option value against the rest of the current selection:
     * values with no matching variant at all are disabled, values that match
     * a variant which is out of stock are flagged sold out but stay usable.
     */
    refreshAvailability() {
      const selected = this.selectedOptions;

      this.fieldsets.forEach((fieldset, fieldsetIndex) => {
        fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
          const probe = selected.slice();
          probe[fieldsetIndex] = input.value;

          const exact = this.variants.filter((variant) =>
            variant.options.every(
              (value, index) => index === fieldsetIndex || probe[index] === null || value === probe[index]
            ) && variant.options[fieldsetIndex] === input.value
          );

          const exists = exact.length > 0;
          const purchasable = exact.some((variant) => variant.available);

          input.disabled = !exists;
          const label = this.querySelector('label[for="' + input.id + '"]');
          if (label) {
            label.setAttribute('data-sold-out', String(exists && !purchasable));
            if (exists && !purchasable) {
              label.title = strings.soldOut || 'Sold out';
            } else {
              label.removeAttribute('title');
            }
          }
        });
      });
    }

    applyVariant(variant) {
      const root = document.getElementById('MainProduct') || document;

      if (this.idField) this.idField.value = variant ? variant.id : '';

      /* Price */
      const priceHolder = root.querySelector('[data-product-price]');
      if (priceHolder && variant) {
        const current = priceHolder.querySelector('[data-price-current]');
        const compare = priceHolder.querySelector('[data-price-compare]');
        if (current) current.textContent = variant.price;
        if (compare) {
          if (variant.on_sale && variant.compare_at_price) {
            compare.textContent = variant.compare_at_price;
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
        priceHolder.classList.toggle('price--on-sale', Boolean(variant.on_sale));
      }

      /* SKU */
      const sku = root.querySelector('[data-product-sku-value]');
      const skuWrap = root.querySelector('[data-product-sku]');
      if (sku && variant) {
        const value = variant.sku || '';
        sku.textContent = value;
        if (skuWrap) skuWrap.hidden = value === '';
      }

      /* Add to bag button */
      const button = root.querySelector('[data-cart-add-button]');
      const label = root.querySelector('[data-cart-add-text]');
      if (button && label) {
        if (!variant) {
          button.disabled = true;
          label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          button.disabled = true;
          label.textContent = strings.soldOut || 'Sold out';
        } else {
          button.disabled = false;
          label.textContent = strings.addToBag || 'Add to bag';
        }
      }

      /* Quantity ceiling, only when Shopify actually tracks and caps stock */
      const quantity = root.querySelector('[data-product-quantity]');
      if (quantity && variant) {
        if (variant.inventory_management && variant.inventory_policy === 'deny') {
          quantity.max = String(variant.inventory_quantity);
          if (Number(quantity.value) > variant.inventory_quantity) {
            quantity.value = String(Math.max(1, variant.inventory_quantity));
          }
        } else {
          quantity.removeAttribute('max');
        }
        const host = quantity.closest('quantity-input');
        if (host && typeof host.updateButtons === 'function') host.updateButtons();
      }

      /* Media */
      if (variant && variant.media_id) {
        const gallery = document.querySelector('product-gallery');
        if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(variant.media_id);
      }

      /* Keep the URL shareable and reloadable at the chosen variant */
      if (variant && this.getAttribute('data-update-url') === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variant: variant.id }, '', url.toString());
      }

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } })
      );
    }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
  }

  /* ------------------------------------------------------------------ *
   * Gallery
   *
   * A scroll-snapping track on mobile, thumbnails on desktop. No library,
   * and it degrades to a plain scrollable list of images.
   * ------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.status = this.querySelector('[data-gallery-status]');
      if (!this.track || this.slides.length === 0) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.showMedia(Number(thumb.getAttribute('data-media-id')));
        });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.setActive(Number(entry.target.getAttribute('data-media-id')), false);
              }
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    }

    showMedia(mediaId) {
      const slide = this.slides.find((el) => Number(el.getAttribute('data-media-id')) === mediaId);
      if (!slide) return;
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      this.setActive(mediaId, true);
    }

    setActive(mediaId, scrollThumb) {
      this.thumbs.forEach((thumb) => {
        const active = Number(thumb.getAttribute('data-media-id')) === mediaId;
        thumb.setAttribute('aria-current', String(active));
        if (active && scrollThumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });

      const index = this.slides.findIndex((el) => Number(el.getAttribute('data-media-id')) === mediaId);
      if (this.status && index >= 0) {
        this.status.textContent = index + 1 + ' / ' + this.slides.length;
      }
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }
})();
